import { useState, useEffect, useRef } from 'react';
import { Ticket, transformTicketResponse } from '../types/ticket';
import { TicketQueryParams, ticketService } from '../services/ticketService';

interface UseTicketsOptions extends TicketQueryParams {
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export function useTickets(
  options: UseTicketsOptions = {
    dateRange: {
      startDate: '',
      endDate: ''
    }
  }
) {
  const [tickets, setTickets] = useState<(Ticket & { created_by?: any; assigned_to?: any })[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    processing: 0,
    resolved: 0,
    closed: 0,
    assigned: 0,
    unassigned: 0,
    active: 0,
    open: 0
  });

  // Use a ref to store the previous options to compare and avoid unnecessary API calls
  const prevOptionsRef = useRef<UseTicketsOptions | null>(null);

  const fetchTickets = async () => {
    setIsLoading(true);

    try {
      const apiParams = {
        startDate: options.dateRange.startDate,
        endDate: options.dateRange.endDate,
        page: options.page,
        limit: options.limit || 12,
        status: options.status,
        search: options.search,
        sortBy: options.sortBy,
        sortOrder: options.sortOrder,
        tags: options.tags,
        requestedBy: options.requestedBy,
        assignedTo: options.assignedTo
      };

      const response = await ticketService.getTicketsByDateRange(apiParams);

      if (Array.isArray(response)) {
        const transformedTickets = response.map((ticket: any) => transformTicketResponse(ticket));
        setTickets(transformedTickets);
        setTotalCount(response.totalCount || response.length);
        setStats({
          total: response.length,
          new: 0,
          processing: 0,
          resolved: 0,
          closed: 0,
          assigned: 0,
          unassigned: 0,
          active: 0,
          open: 0
        });
      } else {
        const transformedTickets = response.tickets.map((ticket: any) =>
          transformTicketResponse(ticket)
        );
        setTickets(transformedTickets);
        setTotalCount(response.totalCount);
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if arrays are equal
  const areArraysEqual = (arr1?: any[], arr2?: any[]) => {
    if (!arr1 && !arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    // For simple arrays of primitives
    if (typeof arr1[0] !== 'object') {
      return arr1.every((item, index) => item === arr2[index]);
    }

    // For arrays of objects, compare by id
    return arr1.every((item, index) => {
      if (
        typeof item === 'object' &&
        item !== null &&
        typeof arr2[index] === 'object' &&
        arr2[index] !== null
      ) {
        return item.id === arr2[index].id;
      }
      return item === arr2[index];
    });
  };

  // Helper function to check if options have changed
  const haveOptionsChanged = () => {
    if (!prevOptionsRef.current) return true;

    const prev = prevOptionsRef.current;

    // Check primitive values
    if (
      prev.page !== options.page ||
      prev.dateRange.startDate !== options.dateRange.startDate ||
      prev.dateRange.endDate !== options.dateRange.endDate ||
      prev.limit !== options.limit ||
      prev.status !== options.status ||
      prev.search !== options.search ||
      prev.sortBy !== options.sortBy ||
      prev.sortOrder !== options.sortOrder
    ) {
      return true;
    }

    // Check arrays
    if (
      !areArraysEqual(prev.tags, options.tags) ||
      !areArraysEqual(prev.requestedBy, options.requestedBy) ||
      !areArraysEqual(prev.assignedTo, options.assignedTo)
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    // Only fetch if options have changed
    if (haveOptionsChanged()) {
      fetchTickets();
      // Update the ref with current options
      prevOptionsRef.current = {
        ...options,
        // Make deep copies of arrays to avoid reference issues
        tags: options.tags ? [...options.tags] : undefined,
        requestedBy: options.requestedBy ? [...options.requestedBy] : undefined,
        assignedTo: options.assignedTo ? [...options.assignedTo] : undefined,
        dateRange: { ...options.dateRange }
      };
    }
  }, [
    options.page,
    options.dateRange.startDate,
    options.dateRange.endDate,
    options.limit,
    options.status,
    options.search,
    options.sortBy,
    options.sortOrder,
    options.tags,
    options.requestedBy,
    options.assignedTo
  ]);

  return {
    tickets,
    isLoading,
    totalCount,
    stats,
    fetchTickets
  };
}
